/**
 * Text widget — heading plus free text.
 *
 * Rendered as plain text, never as HTML: widget content is authored by admins
 * but stored in the same document as everything else, and `dangerouslySetInnerHTML`
 * here would turn a dashboard into a stored-XSS vector for every viewer.
 * Line breaks are preserved with `whitespace-pre-wrap`.
 */

import WidgetFrame from './WidgetFrame';
import type { DashboardWidget } from '../types/dashboardTypes';

interface Props {
  widget: DashboardWidget;
  accentColor: string;
  dividerColor?: string;
}

export default function TextWidget({ widget, accentColor, dividerColor }: Props) {
  const align = widget.config.align ?? 'left';
  const alignClass =
    align === 'center' ? 'text-center items-center' : align === 'right' ? 'text-right items-end' : 'text-left items-start';

  return (
    <WidgetFrame
      title={widget.title}
      status="ready"
      hideHeader
      accentColor={accentColor}
      outlineColor={widget.config.outlineColor ?? dividerColor}
    >
      <div className={`flex h-full flex-col justify-center gap-1 overflow-y-auto px-4 py-3 ${alignClass}`}>
        {widget.config.heading && (
          <h3
            className="text-base font-semibold leading-snug"
            style={{ color: widget.config.color || '#0F172A' }}
          >
            {widget.config.heading}
          </h3>
        )}
        {widget.config.body && (
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-slate-600">
            {widget.config.body}
          </p>
        )}
      </div>
    </WidgetFrame>
  );
}
